import React from 'react'
import { Grid, InputAdornment, IconButton } from '@mui/material';

// Components
import { SelectInput } from '../../../../SelectInput';

// Styles
import {
  Container,
  Body,
  ContainerInfo,
  SectionBottomInfo,
  Input,
  Text,
  CustomFormControlLabel,
  SpanPassword,
  FormLabel,
  FormLabelWithIconBox
} from '../styles'


// Utils
import { useTranslation } from 'react-i18next'
import { ColorsContext } from '../../../../../Context/ColorsContext';
import { PasswordGenerator } from '../../../../atoms/passwordGenerator';

// Permissions
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../../../Context/PermissionsContext';
import { CustomTextField, CustomButtonV2, AntSwitch } from '../../../../../pages/styles';

export const StepOne = ({
  form,
  addRole,
  roleList,
  selectedRole,
  errors,
  loading,
  authTypeRef,
  handleChange,
  handleSetRole,
  handleAuthentication,
  handleValidateEmail,
  validateEmail,
  buttonRef,
  handleGeneratePassword,
  showPasswordGenerator,
  setShowPasswordGenerator,
  handleGenerateClick,
  passwordPolicyData
}) => {
  const { colors } = React.useContext(ColorsContext);
  const ability = useAbility(AbilityContext);
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  return (
    <Container mediumHigh={passwordPolicyData.isActive && passwordPolicyData.passwordSecurity && passwordPolicyData.passwordComments !== ""}>
      <Body>
        <ContainerInfo>
          <Grid container spacing={2} alignItems="flex-end">
              
            <Grid item xs={12} sm={6}>
              <FormLabelWithIconBox>
                <span className="material-symbols-outlined">email</span>
                <FormLabel>{t('system.users.table.email')}</FormLabel>
              </FormLabelWithIconBox>
              <CustomTextField
                value={form.email}
                onChange={e => handleChange(e, 'email')}
                sx={{ width: '100%' }}
                error={errors.email?.hasError}
                helperText={errors.email?.message}
                disabled={loading || addRole}
              />
            </Grid>
            
            <Grid item xs={7} sm={4}>
              <CustomFormControlLabel
                labelPlacement="top"
                control={
                  <AntSwitch
                    activeColor="secondary"
                    checked={authTypeRef}
                    disabled={addRole}
                    onChange={() => {
                      handleAuthentication()
                      setShowPasswordGenerator(false);
                    }} inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
                label={t('system.users.activeDirectory')} 
              />
            </Grid>

            {ability.can("GetUser") &&
              <Grid item xs={5} sm={2} display='flex' justifyContent='flex-start' alignItems='flex-end'>
                <CustomButtonV2 
                  variant="contained" 
                  btnType="primary"
                  onClick={handleValidateEmail}
                  disabled={addRole}
                  sx={{ width: '100%' }}
                >
                  {t('system.users.validate')}
                </CustomButtonV2>
              </Grid>
            }

            <Grid item xs={12} sm={6}>
              <FormLabelWithIconBox>
                <span className="material-symbols-outlined">badge</span>
                <FormLabel>{t('system.users.table.identification')}</FormLabel>
              </FormLabelWithIconBox>
              <CustomTextField
                type={"number"}
                value={form.identification}
                onChange={e => {
                  if (e.target.value >= 0) handleChange(e, 'identification')
                }}
                sx={{ width: '100%' }}
                error={errors.identification?.hasError}
                helperText={errors.identification?.message}
                onKeyDown={(e) => {
                  if (
                    e?.key === "e" ||
                    e?.key === "E" ||
                    e?.key === "-" ||
                    e?.key === "+" ||
                    e?.key === "." ||
                    e?.key === ","
                  ) {
                    e.preventDefault()
                  }
                }}
                disabled={loading || validateEmail || addRole}
              />
            </Grid>

            {ability.can("RoleList") &&
              <Grid item xs={12} sm={6}>
                <FormLabelWithIconBox>
                  <span className="material-symbols-outlined">folder_shared</span>
                  <FormLabel>{t('system.users.table.role')}</FormLabel>
                </FormLabelWithIconBox>
                <SelectInput
                  onChange={(value) => {
                    handleSetRole(value);
                  }}
                  value={selectedRole}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option != null ? option.name : ''}
                  options={roleList.map((obj) => { return obj })}
                  keyValue="name"
                  sx={{ width: '25%' }}
                  error={errors.role?.message}
                  renderInput={(params) => <Input {...params} label={t('system.users.selectRole')} />}
                  disabled={loading || validateEmail}
                  disableCloseOnSelect={false}
                />
              </Grid>
            }
            
            <Grid item xs={12} sm={6}>
              <FormLabelWithIconBox>
                <span className="material-symbols-outlined">person</span>
                <FormLabel>{t('system.users.table.names')}</FormLabel>
              </FormLabelWithIconBox>
              <CustomTextField
                value={form.name}
                onChange={e => handleChange(e, 'name')}
                sx={{ width: '100%' }}
                error={errors.name?.hasError}
                helperText={errors.name?.message}
                disabled={loading || authTypeRef || form.authenticationType || validateEmail || form.disableAD || addRole}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabelWithIconBox>
                <span className="material-symbols-outlined">person</span>
                <FormLabel>{t('system.users.table.lastNames')}</FormLabel>
              </FormLabelWithIconBox>
              <CustomTextField
                value={form.lastName}
                onChange={e => handleChange(e, 'lastName')}
                sx={{ width: '100%' }}
                error={errors.lastName?.hasError}
                helperText={errors.lastName?.message}
                disabled={loading || authTypeRef || form.authenticationType || validateEmail || form.disableAD || addRole}
              />
            </Grid>

            <Grid item xs={12} sm={12} mt={1}>
              <FormLabelWithIconBox>
                <span className="material-symbols-outlined">lock</span>
                <FormLabel>{t('system.users.passwordConfiguration')}</FormLabel>
              </FormLabelWithIconBox>
            </Grid>

            <Grid item xs={12} sm={6} sx={{ alignSelf: 'flex-start' }}>
              <FormLabelWithIconBox>
                <FormLabel>{t('system.users.password')}</FormLabel>
              </FormLabelWithIconBox>
              <CustomTextField
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={e => handleChange(e, 'password')}
                sx={{ width: '100%' }}
                error={errors.password?.hasError}
                helperText={errors.password?.message}
                disabled={loading || authTypeRef || form.authenticationType || validateEmail || addRole}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={loading || authTypeRef || form.authenticationType || validateEmail || addRole}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#b1b7bf' }}>
                          {showPassword ? 'visibility_off' : 'visibility'}
                        </span>
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px', cursor: (loading || authTypeRef || form.authenticationType || validateEmail || addRole) ? 'default' : 'pointer' }}
                onClick={() => {
                  if (!(loading || authTypeRef || form.authenticationType || validateEmail || addRole)) {
                    handleGenerateClick()
                  }
                }}
                ref={buttonRef}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '16px', color: colors.sidebarSelectColor, marginRight: '4px' }}>account_circle</span>
                <span style={{ fontSize: '11px', color: colors.sidebarSelectColor, fontStyle: 'italic', opacity: (loading || authTypeRef || form.authenticationType || validateEmail || addRole) ? 0.5 : 1 }}>{t('system.users.suggestSecurePassword')}</span>
              </div>
              {showPasswordGenerator && (
                <PasswordGenerator
                  onGenerate={handleGeneratePassword}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={6} sx={{ alignSelf: 'flex-start' }}>
              <FormLabelWithIconBox>
                <FormLabel>{t('system.users.passwordConfirmation')}</FormLabel>
              </FormLabelWithIconBox>
              <CustomTextField
                type={showConfirmPassword ? 'text' : 'password'}
                value={form.confirm_password}
                onChange={e => handleChange(e, 'confirm_password')}
                sx={{ width: '100%' }}
                error={errors.confirm_password?.hasError}
                helperText={errors.confirm_password?.message}
                disabled={loading || authTypeRef || form.authenticationType || validateEmail || addRole}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        disabled={loading || authTypeRef || form.authenticationType || validateEmail || addRole}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#b1b7bf' }}>
                          {showConfirmPassword ? 'visibility_off' : 'visibility'}
                        </span>
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {passwordPolicyData.passwordSecurity &&
              <Grid item xs={12} sm={6}>
                <SpanPassword>{passwordPolicyData.passwordComments}</SpanPassword>
              </Grid>
            }

          </Grid>
          {errors.general?.hasError && (
            <SectionBottomInfo>
              <Text>{errors.general?.message}</Text>
            </SectionBottomInfo>
          )}
        </ContainerInfo>
      </Body>
    </Container>
  )
}
